import { MessageBarBody, MessageBarTitle, Spinner, Textarea } from "@fluentui/react-components";
import { connect } from "react-redux";
import { useEffect, useMemo, useRef, useState } from "react";

// component functions
import * as ComponentFunctions from './Payment.Form.Function';
import PaymentImputationsComponent from '../PaymentImputations/PaymentImputations.Component';
import { ImputationPicker } from './Payment.Imputation.Picker';
import { ReceiverPicker } from './Payment.Receiver.Picker';
import { candidateDebts, inPaymentCurrency, parseAmount, suggestedCurrencies, validatePaymentForm } from "../../CashFlow.Form.Logic";
import { AmountInput, CurrencyChips, DateInput, MoreData, PartyChip, SaveErrorBanner, Step } from "../../CashFlow.Form.Bits";
import { GuideDrawer, GuideDrawerForm, GuideDrawerSection } from "../../../../theme/components/GuideDrawer";
import { GuideField, GuideFieldGrid } from "../../../../theme/components/GuideField";
import { GuideMessageBar } from "../../../../theme/components/GuideMessageBar";
import { MEDICAL_CENTER_NAME } from "../../General/Cashflow.General.Functions";
import { currencyOf } from "../../General/Cashflow.Dashboard.Functions";

// components
import PersonSelectorComponent from "../../../../commons/component/Inputs/PersonSelector/PersonSelector.Component";
import ProtocolSelectorComponent from "../../../../commons/component/Inputs/ProtocolSelector/ProtocolSelectorComponent";
import { formatCurrencyAmountWithIssue } from "../../../../commons/currency/Currency.Functions";

// models
import { Person } from "../../../../models/Entities/Person/Person";
import { Protocol } from "../../../../models/Entities/Protocols/Protocol";
import { NewPayment } from "../../../../models/Entities/CashFlow/Payment/NewPayment";
import { Payment } from "../../../../models/Entities/CashFlow/Payment/Payment";
import { DebtPaymentImputation } from "../../../../models/Entities/CashFlow/Payment/DebtPaymentImputation";
import { PaymentAttachmentsOutcome, PaymentAttachmentsRequest } from "../../../../models/Entities/CashFlow/CreditorInvoice/CreditorInvoice";

// facturas y comprobantes del pago (contrato §12.3)
import { InvoicePicker } from "../PaymentInvoices/PaymentInvoices.Picker";
import { PaymentReceiptsPicker, validReceipts } from "../PaymentInvoices/PaymentReceipts.Picker";
import { PaymentAttachmentsResult } from "../PaymentInvoices/PaymentAttachments.Result";
import { UnavailableNotice, useInvoicesText } from "../PaymentInvoices/PaymentInvoices.Bits";
import {
    buildAllocationsPayload,
    hasAttachmentFailures,
    InvoiceSelection,
    suggestInvoicesForPayment,
    validateSelections
} from "../../../../functions/CreditorInvoices.Functions";
import { isGuid } from "../../../../functions/AzureFileUpload.Functions";
import { GuideSkeleton } from "../../../../theme/components/GuideSkeleton";

// styles
import './Payment.Form.Styles.css';

/**
 * Registro de un cobro o un pago. El centro es siempre una de las partes; se
 * pregunta la otra, el importe y a qué deudas se aplica. Desde una deuda, todo
 * viene precargado.
 */
const PaymentFormComponent = (props: ComponentFunctions.Props) => {

    const isReceived = props.FormType === ComponentFunctions.FormType.receiver;
    const isEdit = !!props.CurrentPayment;
    /** Pago de deudas de un acreedor con receptor elegible (pantalla del profesional). */
    const receiverMode = !!props.ImputeToCreditorId && !isReceived && !props.PaymentId;
    /**
     * Cuando el centro PAGA (alta), el receptor del pago ("cobrador") se puede cambiar en todas las
     * pantallas: por defecto es el acreedor de las deudas, que siguen siendo las del acreedor original.
     */
    const payeeMode = !isReceived && !props.PaymentId;
    /** Acreedores de las deudas precargadas (un pago de varias deudas puede ser de varios). */
    const prefillCreditors = useMemo(
        () => (isReceived ? [] : Array.from(new Set((props.PrefillDebts || []).map((debt) => debt.creditor).filter(Boolean)))),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [isReceived, (props.PrefillDebts || []).map((debt) => `${debt.id}:${debt.creditor}`).join(',')]
    );
    const multiCreditor = !receiverMode && prefillCreditors.length > 1;

    // ── Estado ──
    const [counterpart, setCounterpart] = useState<Person | undefined>(undefined);
    const [currency, setCurrency] = useState<string | undefined>(undefined);
    const [amountText, setAmountText] = useState<string>('');
    const [amount, setAmount] = useState<number>(0);
    const [date, setDate] = useState<Date | undefined>(new Date());
    const [imputations, setImputations] = useState<DebtPaymentImputation[]>([]);
    const [invoiceText, setInvoiceText] = useState<string>('');
    const [retentionText, setRetentionText] = useState<string>('');
    const [bankText, setBankText] = useState<string>('');
    const [protocolId, setProtocolId] = useState<number | undefined>(undefined);
    const [comments, setComments] = useState<string>('');
    const [submitted, setSubmitted] = useState<boolean>(false);
    const [currencyTouched, setCurrencyTouched] = useState<boolean>(false);
    const [receiverId, setReceiverId] = useState<string | undefined>(props.ImputeToCreditorId);
    /** El usuario eligió el receptor: ya no sigue al acreedor. */
    const [receiverTouched, setReceiverTouched] = useState<boolean>(false);
    const [confirming, setConfirming] = useState<boolean>(false);
    // Facturas que cubre el pago y comprobantes (sólo pagos a profesionales, contrato §12.3).
    const tri = useInvoicesText();
    const [invoiceSelections, setInvoiceSelections] = useState<InvoiceSelection[]>([]);
    const [receiptFiles, setReceiptFiles] = useState<File[]>([]);
    const [attachRequest, setAttachRequest] = useState<PaymentAttachmentsRequest | undefined>(undefined);
    const [attachOutcome, setAttachOutcome] = useState<PaymentAttachmentsOutcome | undefined>(undefined);
    const [attaching, setAttaching] = useState<boolean>(false);
    /** Hay facturas o comprobantes para después de crear el pago: el cierre lo maneja ese paso. */
    const extrasRef = useRef<boolean>(false);

    const ownerPerson: Person = { personIdentifier: props.OwnerId, id: 0, isDeleted: false, personType: 0, businessName: MEDICAL_CENTER_NAME } as Person;
    const personOf = (identifier: string | undefined): Person | undefined => {
        const found = ComponentFunctions.GetPersonByIdentifier(props.Persons, props.OwnerId, identifier || '');
        if (found || !identifier) return found;
        // El acreedor puede no estar en personas (un profesional): se arma con su nombre (al pagar o desde el estado de cuenta).
        if (!payeeMode && identifier !== props.ImputeToCreditorId) return found;
        return { personIdentifier: identifier, businessName: props.NameOf(identifier) } as Person;
    };

    // ── Carga ──
    useEffect(() => {
        if (!props.isOpen) return;
        props.GetAllPersons();
        // Para elegir el receptor (profesionales además de personas y empresas).
        if (payeeMode) props.GetAllPractitioners(1, 1000, undefined);
        if (props.PaymentId && !props.CurrentPayment) props.GetPaymentById(props.PaymentId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.isOpen, props.PaymentId]);

    // Al abrir en alta: arranca con lo que ya se sabe.
    useEffect(() => {
        if (!props.isOpen || props.PaymentId) return;
        setSubmitted(false);
        setCurrencyTouched(false);
        setConfirming(false);
        setReceiverTouched(false);
        setInvoiceSelections([]);
        setReceiptFiles([]);
        setAttachRequest(undefined);
        setAttachOutcome(undefined);
        setAttaching(false);
        extrasRef.current = false;
        const debt = props.PrefillDebt;
        // Con receptor elegible, la contraparte de las deudas es siempre el acreedor.
        const counterpartId = receiverMode
            ? props.ImputeToCreditorId
            : debt
                ? (isReceived ? debt.debtor : debt.creditor)
                : (props.FixedCounterpart?.personIdentifier ?? props.PrefillCounterpartId);
        setCounterpart(personOf(counterpartId));
        // Receptor por defecto: el acreedor (si las deudas son de varios acreedores, hay que elegirlo).
        if (!isReceived) {
            const creditors = props.ImputeToCreditorId ? [] : Array.from(new Set((props.PrefillDebts || []).map((item) => item.creditor).filter(Boolean)));
            setReceiverId(props.ImputeToCreditorId ?? (creditors.length > 1 ? undefined : counterpartId));
        } else {
            setReceiverId(undefined);
        }
        const prefillCurrency = debt ? currencyOf(debt) : undefined;
        setCurrency(prefillCurrency);
        // Con varias deudas: se suman las de la misma moneda que la primera y se
        // imputa cada una por su pendiente; las de otra moneda quedan como candidatas.
        const round = (value: number) => Math.round(value * 100) / 100;
        const sameCurrency = (props.PrefillDebts || []).filter((item) => currencyOf(item) === prefillCurrency && (item.pendingAmount ?? 0) > 0);
        const pending = round(sameCurrency.reduce((acc, item) => acc + (item.pendingAmount ?? 0), 0));
        setAmount(pending);
        setAmountText(pending > 0 ? String(pending) : '');
        setImputations(sameCurrency.map((item) => ({ debtId: item.id, amount: round(item.pendingAmount ?? 0), exchangeRate: 1 })));
        setDate(new Date());
        setInvoiceText('');
        setRetentionText('');
        setBankText('');
        // El protocolo se hereda si todas las deudas precargadas son del mismo;
        // así el pago queda como salida de plata de ese protocolo.
        const protocols = Array.from(new Set((props.PrefillDebts || []).map((item) => item.protocolId).filter((id): id is number => !!id)));
        setProtocolId(protocols.length === 1 ? protocols[0] : undefined);
        setComments('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.isOpen, (props.PrefillDebts || []).map((item) => item.id).join(','), props.FixedCounterpart?.personIdentifier, props.PrefillCounterpartId, props.Persons.length, props.ImputeToCreditorId]);

    // Edición: se completa con el pago.
    useEffect(() => {
        const payment = props.CurrentPayment;
        if (!payment) return;
        setCounterpart(personOf(isReceived ? payment.payer : payment.receiver));
        setCurrency(payment.currencyCode);
        setAmount(payment.amount);
        setAmountText(String(payment.amount));
        setDate(payment.paymentDate ? new Date(payment.paymentDate) : undefined);
        setInvoiceText(payment.invoiceAmount ? String(payment.invoiceAmount) : '');
        setRetentionText(payment.retentionAmount ? String(payment.retentionAmount) : '');
        setBankText(payment.bankChargesAmount ? String(payment.bankChargesAmount) : '');
        setProtocolId(payment.protocolId ?? undefined);
        setComments(payment.comments || '');
        setImputations([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.CurrentPayment?.id, props.Persons.length]);

    useEffect(() => {
        if (extrasRef.current) return;
        if (props.isSaved && submitted) {
            setSubmitted(false);
            props.onClose();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.isSaved]);

    // Segundo camino de cierre: terminó de guardar (el spinner bajó) sin error.
    // No depende de que la bandera de éxito cambie de valor, que es lo que
    // dejaba el drawer abierto cuando ya venía en true de un guardado anterior.
    const wasSavingRef = useRef(false);
    useEffect(() => {
        const finished = wasSavingRef.current && !props.isSaving;
        wasSavingRef.current = props.isSaving;
        if (extrasRef.current) return;
        if (finished && submitted && !props.failOnSaving) {
            setSubmitted(false);
            props.onClose();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.isSaving, props.failOnSaving]);

    useEffect(() => {
        if (props.failOnSaving) setSubmitted(false);
    }, [props.failOnSaving]);

    // ── Sugerencias y deudas candidatas ──
    const suggested = useMemo(
        () => suggestedCurrencies({ counterpartId: counterpart?.personIdentifier, debts: props.AllDebts, payments: props.AllPayments }),
        [counterpart?.personIdentifier, props.AllDebts, props.AllPayments]
    );

    useEffect(() => {
        if (!isEdit && !currencyTouched && !props.PrefillDebt) setCurrency(suggested[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [suggested.join(','), isEdit]);

    // Las deudas candidatas siguen siempre al acreedor original (o a los acreedores de las deudas precargadas), nunca al receptor.
    const candidates = useMemo(
        () => (multiCreditor
            ? candidateDebts(props.CandidateDebts, undefined, (debt) => debt.creditor).filter((debt) => prefillCreditors.includes(debt.creditor))
            : candidateDebts(props.CandidateDebts, counterpart?.personIdentifier, (debt) => (isReceived ? debt.debtor : debt.creditor))),
        [props.CandidateDebts, counterpart?.personIdentifier, isReceived, multiCreditor, prefillCreditors]
    );

    // Alta de pago sin precarga: mientras el usuario no elija otro receptor, el receptor es el acreedor elegido.
    useEffect(() => {
        if (!payeeMode || receiverTouched || multiCreditor || props.ImputeToCreditorId) return;
        setReceiverId(counterpart?.personIdentifier);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [counterpart?.personIdentifier, payeeMode, multiCreditor]);

    // Si cambia la contraparte o la moneda, lo imputado ya no aplica.
    const candidateKey = candidates.map((debt) => debt.id).join(',');
    useEffect(() => {
        setImputations((current) => current.filter((item) => candidates.some((debt) => debt.id === item.debtId)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [candidateKey]);

    // ── Facturas y comprobantes (pago a un profesional) ──
    /** El acreedor es un profesional: el del estado de cuenta o la contraparte de las deudas. */
    const practitionerCreditorId = !isEdit && !isReceived
        ? (props.ImputeToCreditorId || (counterpart?.personIdentifier && props.IsPractitioner?.(counterpart.personIdentifier) ? counterpart.personIdentifier : undefined))
        : undefined;
    const creditorInvoices = practitionerCreditorId ? props.CreditorInvoicesOf?.(practitionerCreditorId) : undefined;
    const canAttachReceipts = !!practitionerCreditorId && isGuid(practitionerCreditorId) && !!props.AttachToPayment;

    useEffect(() => {
        if (props.isOpen && practitionerCreditorId) props.LoadCreditorInvoices?.(practitionerCreditorId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.isOpen, practitionerCreditorId]);

    // Liquidaciones que se están pagando: las de las deudas imputadas (y las precargadas).
    const payingSettlementIds = useMemo(() => {
        const ids = imputations
            .map((item) => (props.CandidateDebts || []).find((debt) => debt.id === item.debtId)?.settlementId)
            .concat((props.PrefillDebts || []).map((debt) => debt.settlementId));
        return Array.from(new Set(ids.filter((id): id is number => typeof id === 'number')));
    }, [imputations, props.CandidateDebts, props.PrefillDebts]);

    const suggestedInvoices = useMemo(
        () => suggestInvoicesForPayment(creditorInvoices?.data ?? [], payingSettlementIds),
        [creditorInvoices?.data, payingSettlementIds]
    );

    // ── Validación ──
    const errors: Record<string, string> = {
        ...validatePaymentForm({ counterpartId: counterpart?.personIdentifier, currency, amount, date, imputations }),
        ...(payeeMode && !receiverId ? { receiver: 'Elegí a quién se le registra el pago.' } : {}),
        ...(practitionerCreditorId && Object.keys(validateSelections(invoiceSelections)).length > 0 ? { invoices: 'invalid' } : {})
    };
    const showError = (key: string) => (submitted ? errors[key] : undefined);
    const canSave = Object.keys(errors).length === 0 && !props.isSaving;

    // ── Guardar ──
    const save = () => {
        setSubmitted(true);
        if (Object.keys(errors).length > 0 || !counterpart || !currency || !date) return;

        const payer = isReceived ? counterpart.personIdentifier : props.OwnerId;
        const receiver = isReceived ? props.OwnerId : (payeeMode ? (receiverId || counterpart.personIdentifier) : counterpart.personIdentifier);

        // Pagar a otro receptor se confirma antes de guardar: el pago queda a su
        // nombre pero cancela la deuda del acreedor.
        if (payeeMode && otherReceiver && !confirming) {
            setConfirming(true);
            return;
        }
        const optional = (text: string) => { const value = parseAmount(text); return value > 0 ? value : undefined; };

        const base = {
            payer, receiver, currencyCode: currency, amount,
            invoiceAmount: optional(invoiceText), retentionAmount: optional(retentionText), bankChargesAmount: optional(bankText),
            paymentDate: date, imputationDate: date,
            comments: comments || undefined, protocolId: protocolId ?? null
        };

        if (!props.CurrentPayment) {
            // El picker trabaja en moneda de la deuda; la API espera el importe en moneda del pago (lo aplicado a la deuda es amount / exchangeRate).
            const debtsPaymentImputation = imputations.map((item) => ({ ...item, amount: Math.round(inPaymentCurrency(item) * 100) / 100 }));
            const newPayment: NewPayment = { ...base, debtsPaymentImputation };
            const receipts = canAttachReceipts ? validReceipts(receiptFiles) : [];
            const allocations = practitionerCreditorId && props.AttachToPayment ? buildAllocationsPayload(invoiceSelections) : [];
            const hasExtras = !!practitionerCreditorId && (allocations.length > 0 || receipts.length > 0);
            extrasRef.current = hasExtras;
            const created = props.CreatePayment(newPayment);
            if (hasExtras && created && typeof (created as Promise<unknown>).then === 'function') {
                attachAfterCreate(created as Promise<Payment | undefined>, practitionerCreditorId!, allocations, receipts);
            } else {
                extrasRef.current = false;
            }
        } else {
            const payment: Payment = { ...props.CurrentPayment, ...base, debtsPaymentImputation: [] };
            props.UpdatePayment(payment);
        }
    };

    /** Con el id del pago nuevo: facturas (PUT) y comprobantes (subida + registro). El pago ya existe pase lo que pase. */
    const attachAfterCreate = async (
        created: Promise<Payment | undefined>,
        creditorId: string,
        allocations: ReturnType<typeof buildAllocationsPayload>,
        receipts: File[]
    ) => {
        const payment = await created;
        if (!payment?.id || !props.AttachToPayment) {
            // No se creó: queda el error de guardado de siempre.
            extrasRef.current = false;
            return;
        }
        const request: PaymentAttachmentsRequest = {
            paymentId: payment.id,
            creditorId,
            allocations: allocations.length > 0 ? allocations : undefined,
            receipts
        };
        setAttachRequest(request);
        setAttaching(true);
        const outcome = await props.AttachToPayment(request);
        setAttaching(false);
        setAttachOutcome(outcome);
        if (!hasAttachmentFailures(outcome) && (outcome.allocations?.warnings ?? []).length === 0) {
            // Todo bien y sin avisos: se cierra como siempre.
            extrasRef.current = false;
            setSubmitted(false);
            props.onClose();
        }
    };

    const retryAttachments = async () => {
        if (!attachRequest || !attachOutcome || !props.RetryPaymentAttachments) return;
        setAttaching(true);
        const outcome = await props.RetryPaymentAttachments(attachRequest, attachOutcome);
        setAttaching(false);
        setAttachOutcome(outcome);
    };

    const close = () => { extrasRef.current = false; setSubmitted(false); setConfirming(false); props.onClose(); };

    const changeReceiver = (id: string) => { setReceiverId(id); setReceiverTouched(true); setConfirming(false); };

    // ── Textos ──
    const kind = isReceived ? 'cobro' : 'pago';
    const kindLabel = isReceived ? 'Cobro' : 'Pago';
    const title = isEdit ? `Editar ${kind}` : (isReceived ? 'Registrar cobro' : 'Registrar pago');
    const whoQuestion = isReceived ? '¿Quién nos paga?' : '¿A quién le pagamos?';
    const counterpartRole = isReceived ? 'Paga' : 'Cobra';
    const centerRole = isReceived ? 'Cobra' : 'Paga';
    /** Acreedores cuyas deudas cancela el pago. */
    const creditorIds = multiCreditor ? prefillCreditors : (counterpart?.personIdentifier ? [counterpart.personIdentifier] : []);
    const counterpartName = multiCreditor
        ? prefillCreditors.map((id) => props.NameOf(id)).join(', ')
        : (counterpart ? props.NameOf(counterpart.personIdentifier) : undefined);
    const isCounterpartFixed = !!props.FixedCounterpart || !!props.PrefillDebt || receiverMode;
    const receiverName = payeeMode ? (receiverId ? props.NameOf(receiverId) : undefined) : counterpartName;
    const otherReceiver = payeeMode && !!receiverId && creditorIds.length > 0 && (creditorIds.length > 1 || receiverId !== creditorIds[0]);
    const payeeDefaultId = creditorIds.length === 1 ? creditorIds[0] : '';
    /** "Profesional" o "Persona o empresa" (línea secundaria de la tarjeta). */
    const partyKindOf = (identifier: string | undefined): string | undefined => {
        if (!identifier) return undefined;
        if (identifier === props.OwnerId) return 'Centro';
        return props.IsPractitioner?.(identifier) ? 'Profesional' : 'Persona o empresa';
    };
    const payeeOptions = useMemo(() => {
        const base = props.ReceiverOptionsFor ? props.ReceiverOptionsFor(payeeDefaultId, payeeDefaultId ? props.NameOf(payeeDefaultId) : '') : props.ReceiverOptions;
        return base;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.ReceiverOptionsFor, props.ReceiverOptions, payeeDefaultId, props.Persons.length]);

    const prefillSettlements = Array.from(new Set((props.PrefillDebts || []).map((item) => item.settlementId).filter(Boolean)));

    const renderForm = () => (
        <GuideDrawerForm className="cff-root" testId="payment-form">
            {props.failOnSaving && (
                <SaveErrorBanner title={`No se pudo guardar el ${kind}.`} message={props.error?.ErrorMessage || 'Probá de nuevo.'} items={props.error?.Errors} />
            )}

            {props.PrefillDebt && !isEdit && (
                <GuideMessageBar intent="info" layout="multiline">
                    <MessageBarBody>
                        <MessageBarTitle>
                            {props.PrefillDebts.length > 1
                                ? `${kindLabel} de ${props.PrefillDebts.length} deudas`
                                : `${kindLabel} de la deuda #${props.PrefillDebt.id}`}
                            {prefillSettlements.length > 0 ? ` · ${prefillSettlements.map((id) => `LIQ-${id}`).join(', ')}` : ''}
                        </MessageBarTitle>
                        Contraparte, moneda e importe ya vienen cargados con lo pendiente. Si {isReceived ? 'cobraste' : 'pagaste'} otra cosa, cambiá el importe.
                    </MessageBarBody>
                </GuideMessageBar>
            )}

            {confirming && otherReceiver && (
                <GuideMessageBar intent="warning" layout="multiline" data-testid="payment-receiver-confirmation">
                    <MessageBarBody>
                        <MessageBarTitle>Confirmá el receptor del pago</MessageBarTitle>
                        El pago se registrará a nombre de <b>{receiverName}</b> y se descontará de la deuda de <b>{counterpartName}</b>.
                        {' '}Si está bien, tocá «Confirmar y registrar»; si no, cambiá el receptor.
                    </MessageBarBody>
                </GuideMessageBar>
            )}

            {payeeMode && (
                <Step title="¿A quién le pagamos?" error={showError('counterpart')} testId="payment-receiver-step">
                    <div className="cff-parties cff-payee">
                        {isCounterpartFixed || multiCreditor ? (
                            <PartyChip role="Deuda de" name={counterpartName || '—'} square detail={multiCreditor ? 'Varios acreedores' : partyKindOf(counterpart?.personIdentifier)} />
                        ) : (
                            <div className="cff-parties__selector" data-testid="payment-creditor-selector">
                                <PersonSelectorComponent
                                    personSelected={counterpart ? [counterpart.personIdentifier] : []}
                                    selectionLimit={1}
                                    isRequired
                                    fieldLabel="Deuda de (acreedor)"
                                    CustomAditionalPersonsList={[ownerPerson]}
                                    onChangeSelection={(selected: Person[]) => setCounterpart(selected[0])}
                                />
                            </div>
                        )}
                        <span className="cff-parties__arrow" aria-hidden>→</span>
                        <PartyChip role={centerRole} name={MEDICAL_CENTER_NAME} square detail="Centro" />
                    </div>
                    <GuideField
                        className="cff-payee"
                        label="Receptor del pago (cobrador)"
                        required
                        error={showError('receiver')}
                        hint={multiCreditor && !receiverId
                            ? <>Las deudas son de varios acreedores ({counterpartName}): elegí a nombre de quién se registra el pago.</>
                            : otherReceiver
                                ? <>Se registra a nombre de {receiverName}, pero cancela deudas de {counterpartName}.</>
                                : <>Por defecto, {counterpartName || 'el acreedor de las deudas'}. Elegí otra persona, empresa o profesional si el pago se hace a nombre de otro.</>}
                    >
                        <ReceiverPicker
                            options={payeeOptions}
                            value={receiverId}
                            defaultId={payeeDefaultId}
                            onChange={changeReceiver}
                        />
                    </GuideField>
                </Step>
            )}

            {/* Con quién */}
            {!payeeMode && <Step title={whoQuestion} error={showError('counterpart')}>
                <div className="cff-parties">
                    {isEdit || isCounterpartFixed ? (
                        <PartyChip role={counterpartRole} name={counterpartName || '—'} />
                    ) : (
                        <div className="cff-parties__selector">
                            <PersonSelectorComponent
                                personSelected={counterpart ? [counterpart.personIdentifier] : []}
                                selectionLimit={1}
                                isRequired
                                fieldLabel={counterpartRole}
                                CustomAditionalPersonsList={[ownerPerson]}
                                onChangeSelection={(selected: Person[]) => setCounterpart(selected[0])}
                            />
                        </div>
                    )}
                    <span className="cff-parties__arrow" aria-hidden>→</span>
                    <PartyChip role={centerRole} name={MEDICAL_CENTER_NAME} />
                </div>
            </Step>}

            {/* Cuánto y cuándo */}
            <Step title="¿Cuánto y cuándo?">
                <GuideField label="Moneda" required error={showError('currency')}>
                    <CurrencyChips suggested={suggested} value={currency} onChange={(code) => { setCurrencyTouched(true); setCurrency(code); }} disabled={isEdit} />
                </GuideField>
                <GuideFieldGrid>
                    <GuideField label="Importe" required error={showError('amount')}
                        hint={amount > 0 && currency ? <span className="cff-num">{formatCurrencyAmountWithIssue(amount, currency)}</span> : undefined}>
                        <AmountInput value={amountText} currency={currency} autoFocus={!isEdit && !!counterpart && !props.PrefillDebt}
                            onChange={(text, value) => { setAmountText(text); setAmount(value); }} />
                    </GuideField>
                    <GuideField label="Fecha" required error={showError('date')} hint="Por defecto, hoy.">
                        <DateInput value={date} onChange={setDate} />
                    </GuideField>
                </GuideFieldGrid>
                <MoreData label="Detalle del comprobante (opcional)" open={!!invoiceText || !!retentionText || !!bankText}>
                    <GuideFieldGrid>
                        <GuideField label="Importe de factura">
                            <AmountInput value={invoiceText} currency={currency} onChange={(text) => setInvoiceText(text)} />
                        </GuideField>
                        <GuideField label="Retenciones">
                            <AmountInput value={retentionText} currency={currency} onChange={(text) => setRetentionText(text)} />
                        </GuideField>
                        <GuideField label="Gastos bancarios">
                            <AmountInput value={bankText} currency={currency} onChange={(text) => setBankText(text)} />
                        </GuideField>
                    </GuideFieldGrid>
                </MoreData>
            </Step>

            {/* Qué deudas cancela */}
            {!isEdit && (
                <Step title="¿Qué deudas cancela?" error={showError('imputations')}
                    hint={counterpart && currency
                        ? <>Pendientes de {counterpartName}, más viejas primero. Podés dejar parte sin imputar.</>
                        : <>Primero elegí la contraparte y la moneda.</>}>
                    {counterpart && currency ? (
                        <ImputationPicker
                            debts={candidates}
                            paymentAmount={amount}
                            paymentCurrency={currency}
                            counterpartName={counterpartName || ''}
                            value={imputations}
                            onChange={setImputations}
                        />
                    ) : null}
                </Step>
            )}

            <MoreData open={!!protocolId || !!comments}>
                <GuideField label="Protocolo" hint="Si el movimiento pertenece a un estudio.">
                    <ProtocolSelectorComponent
                        selectionLimit={1}
                        selectedProtocols={protocolId === undefined ? [] : [{ id: protocolId } as Protocol]}
                        onChangeSelection={(selected: Protocol[] | undefined) => setProtocolId((selected || [])[0]?.id)}
                    />
                </GuideField>
                <GuideField label="Comentarios" hint="Referencia de la transferencia, número de recibo, lo que ayude a encontrarlo después.">
                    <Textarea value={comments} onChange={(_e, data) => setComments(data.value)} resize="vertical" />
                </GuideField>
            </MoreData>

            {practitionerCreditorId && (
                <GuideDrawerSection title={tri('picker.title')} description={tri('picker.description')} testId="payment-invoices-section">
                    {renderInvoices()}
                </GuideDrawerSection>
            )}

            {canAttachReceipts && (
                <GuideDrawerSection title={tri('receipts.title')} description={tri('receipts.description')} testId="payment-receipts-section">
                    <PaymentReceiptsPicker value={receiptFiles} onChange={setReceiptFiles} disabled={props.isSaving} />
                </GuideDrawerSection>
            )}

            {isEdit && props.PaymentId && (
                <GuideDrawerSection>
                    <PaymentImputationsComponent paymentId={props.PaymentId} />
                </GuideDrawerSection>
            )}
        </GuideDrawerForm>
    );

    function renderInvoices() {
        const status = creditorInvoices?.status;
        if (status === 'unavailable') return <UnavailableNotice what={tri('unavailableWhat')} testId="payment-invoices-unavailable" />;
        if (status === 'failed' && !creditorInvoices?.data) {
            return (
                <GuideMessageBar intent="error" layout="multiline">
                    <MessageBarBody>
                        <MessageBarTitle>{tri('picker.loadError')}</MessageBarTitle>
                        {creditorInvoices?.error?.ErrorMessage}
                    </MessageBarBody>
                </GuideMessageBar>
            );
        }
        if (!creditorInvoices || ((status === 'loading' || status === 'idle') && !creditorInvoices.data)) {
            return <GuideSkeleton rows={2} label={tri('picker.loading')} data-testid="payment-invoices-loading" />;
        }
        return (
            <InvoicePicker
                invoices={suggestedInvoices}
                value={invoiceSelections}
                onChange={setInvoiceSelections}
                suggestedSettlementIds={payingSettlementIds}
                settlementNameOf={(id) => props.SettlementNameOf?.(id) ?? `LIQ-${id}`}
                disabled={props.isSaving}
                testId="payment-form-invoices"
            />
        );
    }

    // Después de crear el pago: resultado de facturas y comprobantes, con "Reintentar".
    const afterSave = attaching || !!attachOutcome;
    const afterSaveFailed = hasAttachmentFailures(attachOutcome);
    const renderAfterSave = () => (
        <GuideDrawerForm className="cff-root" testId="payment-after-save">
            <PaymentAttachmentsResult outcome={attachOutcome} working={attaching} allocationCount={attachRequest?.allocations?.length ?? 0} />
        </GuideDrawerForm>
    );

    const loadingEdit = !!props.PaymentId && !props.CurrentPayment;
    const summary = amount > 0 && currency && counterpartName
        ? <>{isReceived ? `${counterpartName} paga` : `Pagamos a ${receiverName || counterpartName}`} <b className="cff-num">{formatCurrencyAmountWithIssue(amount, currency)}</b>{otherReceiver ? ` · deuda de ${counterpartName}` : ''}</>
        : undefined;

    return (
        <GuideDrawer
            open={props.isOpen}
            size="large"
            title={title}
            eyebrow={isEdit && props.PaymentId ? `Flujo de caja · ${kindLabel} #${props.PaymentId}` : 'Flujo de caja'}
            subtitle={summary}
            onDismiss={close}
            closeLabel="Cerrar"
            testId="payment-form-drawer"
            preventDismiss={attaching}
            secondaryAction={afterSave
                ? { label: tri('afterSave.close'), disabled: attaching }
                : { label: 'Cancelar', disabled: props.isSaving }}
            primaryAction={afterSave
                ? (afterSaveFailed
                    ? { label: tri('afterSave.retry'), onClick: retryAttachments, loading: attaching, testId: 'payment-attachments-retry' }
                    : undefined)
                : {
                    label: isEdit ? 'Guardar cambios' : (isReceived ? 'Registrar cobro' : (confirming && otherReceiver ? 'Confirmar y registrar' : 'Registrar pago')),
                    onClick: save,
                    loading: props.isSaving,
                    disabled: submitted ? !canSave : false,
                }}
        >
            {loadingEdit ? <div className="cff-root cff-state"><Spinner size="small" /> Cargando {kind}…</div> : (afterSave ? renderAfterSave() : renderForm())}
        </GuideDrawer>
    );
};

export default connect(
    ComponentFunctions.mapStateToProps,
    ComponentFunctions.mapDispatchToProps
)(PaymentFormComponent as any);
